<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script setup lang="ts">
import type {
  OCGCurveType,
  OCGTerritory,
} from '@src/store/analysis/data-typing';

import type { TableData } from './AnalysisTable.vue';

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import { useAnalysisStore } from '../../store/analysis';
import AnalysisTable from './AnalysisTable.vue';

const props = defineProps<{
  targetMarkers: Record<OCGCurveType, OCGTerritory[]>;
  viewerID: string;
}>();

const analysisStore = useAnalysisStore();
const currentIndex = ref(0);
const slideWidth = ref(0);
const tableData = ref<TableData[]>([]);
const isMaximized = ref(true);
const carouselContent = ref<HTMLElement>();
const carouselItems = ref<HTMLElement[]>([]);

const markerEntries = computed(() => {
  if (!props.targetMarkers || typeof props.targetMarkers !== 'object')
    return [];

  return (
    Object.entries(props.targetMarkers) as [OCGCurveType, OCGTerritory[]][]
  ).flatMap(([biomarker, territories]) =>
    territories.map(territory => ({ biomarker, territory })),
  );
});

const viewerIndx = computed(() => {
  const viewportStateStore = useViewportStateStore();
  return viewportStateStore.getSelectedImageIndices(props.viewerID);
});

const calculateSlideWidth = () => {
  if (isMaximized.value) return;

  if (!carouselItems.value || carouselItems.value.length === 0) return;

  const item = carouselItems.value[0];
  if (item) {
    slideWidth.value
      = item.clientWidth
        + Number.parseInt(getComputedStyle(item).marginLeft, 10)
        + Number.parseInt(getComputedStyle(item).marginRight, 10);
  }
};

const goToSlide = (index: number) => {
  if (index >= 0 && index < tableData.value.length) {
    currentIndex.value = index;
  }
};

// const slideCount = () => tableData.value.length;

// const toggleMaximize = () => {
//   isMaximized.value = !isMaximized.value;
//   if (!isMaximized.value) {
//     nextTick(() => {
//       calculateSlideWidth();
//       if (currentIndex.value >= tableData.value.length) {
//         currentIndex.value = 0;
//       }
//     });
//   }
// };

const initTables = () => {
  try {
    if (!props.targetMarkers || Object.keys(props.targetMarkers).length === 0) {
      console.warn('No target markers provided.');
      return;
    }

    const newTableData: TableData[] = [];
    const { sliceIdx, seriesIdx } = viewerIndx.value;
    const currentStudyStore = useCurrentStudyStore();

    const seriesUID = currentStudyStore.getSeriesUID(seriesIdx);
    const biomarkerContainer = analysisStore.getBiomarkerContainer();

    if (!biomarkerContainer) {
      console.error('Biomarker container is not available.');
      return;
    }

    for (
      let entryIndex = 0;
      entryIndex < markerEntries.value.length;
      entryIndex++
    ) {
      const { biomarker, territory } = markerEntries.value[entryIndex];
      if (!biomarker || !territory) {
        console.warn(
          `Skipping marker at index ${entryIndex} because it's invalid.`,
        );
        continue;
      }

      const bioCurve
        = biomarkerContainer?.getBiomarkerCurveBySliceIndexTypeAndTerritory(
          seriesUID,
          sliceIdx,
          biomarker,
          territory,
        );

      if (!bioCurve) {
        console.warn(
          `No biomarker curve found for slice ${sliceIdx}, biomarker ${biomarker}, territory ${territory}. Skipping.`,
        );
        continue;
      }

      newTableData.push(bioCurve.getStructuredDataForTable());
    }

    if (newTableData.length === 0) {
      console.warn('No table data generated from target markers.');
      return;
    }

    tableData.value = newTableData;
    currentIndex.value = 0;

    if (!isMaximized.value) {
      nextTick(() => {
        goToSlide(currentIndex.value);
        calculateSlideWidth();
      });
    }
  }
  catch (error) {
    console.error('Error initializing tables:', error);
  }
};

onMounted(() => {
  initTables();
  if (!isMaximized.value) {
    calculateSlideWidth();
  }
  window.addEventListener('resize', calculateSlideWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', calculateSlideWidth);
});

watch(
  () => props.targetMarkers,
  () => {
    initTables();
  },
  { deep: true },
);

watch(
  () => [viewerIndx.value.sliceIdx, viewerIndx.value.seriesIdx],
  () => {
    initTables();
  },
);
</script>

<template>
  <div class="analysis-component-container">
    <!-- Maximized View (Vertical Stack) -->
    <div v-if="isMaximized" class="vertical-stack">
      <div
        v-for="(table, index) in tableData"
        :key="index"
        class="vertical-table-item"
      >
        <div class="vertical-table-header">
          <h6 class="table-title-text">
            {{ table.title }}
          </h6>
        </div>
        <AnalysisTable :data="table" />
      </div>
    </div>

    <!-- Mini View (Carousel) -->
    <div v-else class="carousel-container">
      <div class="tabs-header">
        <div class="tabs-navigation">
          <h6
            v-for="(table, index) in tableData"
            :key="index"
            class="tab-text"
            :class="{ active: currentIndex === index }"
            @click="goToSlide(index)"
          >
            {{ table.title }}
          </h6>
        </div>
      </div>
      <div class="carousel">
        <div
          ref="carouselContent"
          class="carousel-content"
          :style="{ transform: `translateX(-${currentIndex * slideWidth}px)` }"
        >
          <div
            v-for="(table, index) in tableData"
            :key="index"
            ref="carouselItems"
            class="carousel-item"
          >
            <AnalysisTable :data="table" />
          </div>
        </div>
      </div>
    </div>

    <!-- <div class="toggle-header" @click="toggleMaximize" v-if="slideCount() > 1">
      <h6 class="label" v-if="isMaximized">Collapse</h6>
      <h6 class="label" v-else>Expand Table</h6>

      <i
        class="fa-solid toggle-icon"
        :class="isMaximized ? 'fa-chevron-up' : 'fa-chevron-down'"
        title="Toggle View"
      ></i>
    </div> -->
  </div>
</template>

<style scoped>
.analysis-component-container {
  width: 100%;
  position: relative;
}

.toggle-header {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  align-items: center;
  padding: 2px 5px;
  background: transparent;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.toggle-header:hover {
  opacity: 1;
}

.toggle-icon {
  color: var(--md-on-surface);
  font-size: 16px;
}

.vertical-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.vertical-table-item {
  width: 100%;
}

.vertical-table-header {
  background-color: var(--md-surface-container);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--md-surface-container-highest);
  border-radius: 5px;
  padding: 2px 10px;
  margin-bottom: 5px;
}

.table-title-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--md-on-surface);
  text-align: center;
}

.carousel-container {
  width: 100%;
  position: relative;
}

.tabs-header {
  background-color: var(--md-surface-container);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--md-surface-container-highest);
  border-radius: 5px;
  padding: 2px 2px;
  margin-bottom: 5px;
  overflow-x: hidden;
  white-space: nowrap;
  text-align: center;
}

.tabs-navigation {
  display: inline-block;
  display: flex;
  justify-content: center;
  gap: 5%;
  padding: 0px 10px;
}

.tab-text {
  display: inline-block;
  padding: 0 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  position: relative;
}

.tab-divider {
  display: inline-block;
  margin: 0 2px;
  color: rgba(255, 255, 255, 0.3);
}

.tab-text.active {
  color: #000;
  font-weight: 500;
}

.tab-text:hover:not(.active) {
  color: #333;
}

.carousel {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.carousel-content {
  display: flex;
  transition: transform 0.5s ease;
}

.carousel-item {
  min-width: 100%;
  flex: 0 0 auto;
}

.tab-text {
  color: #aaa;
}

.tab-text.active {
  color: #fff;
}

.tab-text:hover:not(.active) {
  color: #ddd;
}
</style>
